import axios, { type AxiosInstance } from 'axios';
import { AUTH_REQUEST_TIMEOUT_MS } from './config';
import { csrfHeaders } from './csrf';
import { authEnvironment, type AuthEnvironment } from './environment';
import type { AuthNamespace } from './namespace';
import type { AuthMode } from './tokenStore';

export interface RefreshTokenResponse {
    accessToken: string;
    /** В cookie-режиме пустая строка: refresh — в httpOnly cookie. */
    refreshToken: string;
}

export interface RefreshClientOptions {
    /** База Account-эндпоинтов — `config.application.apiUrl` приложения. */
    apiUrl: string;
    withCredentials: boolean;
    namespace: Pick<AuthNamespace, 'csrfCookie' | 'csrfHeader'>;
    /**
     * Транспорт без интерцепторов клиента приложения — иначе 401 ротации снова зашёл бы в обработчик 401. По умолчанию —
     * экземпляр axios по умолчанию, как и раньше.
     */
    transport?: Pick<AxiosInstance, 'post'>;
    env?: Partial<Pick<AuthEnvironment, 'cookies'>>;
}

export interface RefreshClient {
    /**
     * POST `/Account/RefreshToken`. Body-режим: refresh в теле. Cookie-режим: в теле только `deviceId`, refresh-cookie
     * прикладывает `withCredentials`, заголовок CSRF — из cookie namespace (double-submit).
     */
    postRefreshToken(refreshToken: string | null, deviceId: string, mode: AuthMode): Promise<RefreshTokenResponse>;
}

export function createRefreshClient({
    apiUrl,
    withCredentials,
    namespace,
    transport,
    env,
}: RefreshClientOptions): RefreshClient {
    const resolved = authEnvironment(env);
    return {
        async postRefreshToken(refreshToken, deviceId, mode) {
            const cookieMode = mode === 'cookie';
            const response = await (transport ?? axios).post<RefreshTokenResponse>(
                `${apiUrl}/Account/RefreshToken`,
                cookieMode ? { deviceId } : { refreshToken: refreshToken ?? '', deviceId },
                {
                    withCredentials,
                    headers: cookieMode ? csrfHeaders(namespace, resolved.cookies()) : {},
                    timeout: AUTH_REQUEST_TIMEOUT_MS,
                },
            );
            return response.data;
        },
    };
}
